import createREGL from 'regl';
import type { Buffer, Elements, Regl } from 'regl';
import {
  PATTERN_ID_V2,
  WARP2_FRAG,
  WARP2_VERT,
  buildSurfaceGeometry,
  buildSurfacePatchGeometry,
  computeHomography,
  fineWarpUniformsV2,
  fitModeToUniform,
  invert3x3,
  rasterizeMask,
  sourceRegionTargetAspect,
  toColumnMajor,
  type Corners,
  type MediaFitMode,
  type ProceduralNameV2,
  type Surface,
  type FineWarpUniformsV2,
} from '@cornerstone/shared';

// =============================================================================
// Admin Calibration v2 렌더러 — PLAN §4.1~§4.3.
// shared warpCore(WARP2_VERT/FRAG + buildSurfaceGeometry)를 regl에 바인딩한다.
//
//   - surface 루프 draw (MVP는 1개지만 배열 그대로 순회).
//   - mesh 테셀레이션: buildSurfaceGeometry가 준 positions/uvs/indices를
//     동적 buffer로 업로드 (surface별 geometry 캐시).
//   - 마스크: rasterizeMask로 offscreen canvas에 흰색 알파를 그려 텍스처 업로드.
//     드래그 중에는 1/4 해상도(draft), 드롭 시 풀해상도로 재래스터.
//   - 미디어 텍스처: 이미지/비디오 엘리먼트를 uTex로 (§4.3). 비디오는 매 draw
//     마다 subimage로 현재 프레임을 갱신한다.
//
// 기존 admin renderer/warp.ts(v1)는 수정하지 않는다 — 최종 정리 단계에서 삭제.
// =============================================================================

/** 캔버스에 표시할 미디어 소스 (§4.3 admin 미디어 프리뷰). */
export type MediaTextureSourceV2 = {
  element: HTMLVideoElement | HTMLImageElement;
  width: number;
  height: number;
  fit: MediaFitMode;
};

export type WarpV2DrawInput = {
  surfaces: Surface[];
  pattern: ProceduralNameV2;
  heatmap: boolean;
  bypassWarp: boolean;
  media: MediaTextureSourceV2 | null;
  /** 마스크 래스터 품질 — 드래그 중 'draft'(1/4), 드롭 후 'full'. */
  maskQuality: 'draft' | 'full';
};

export type WarpV2Renderer = {
  draw: (input: WarpV2DrawInput) => void;
  resize: () => void;
  destroy: () => void;
};

/** feather px의 기준 출력 해상도 — preset outputResolutionW 기본값과 동일. */
const FEATHER_REF_WIDTH = 3840;
/** 마스크 알파 텍스처의 최대 가로 해상도 (PLAN §4.1 — 1/2 출력이면 충분). */
const MASK_MAX_WIDTH = 2048;

const UNIT_SOURCE_QUAD: Corners = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

// identity는 row/column-major가 동일 — bypass 시 사용.
const IDENTITY_HINV = [1, 0, 0, 0, 1, 0, 0, 0, 1];

type DrawUniforms = FineWarpUniformsV2 & {
  uHinv: number[];
  uUseMesh: number;
  uPattern: number;
  uUseTexture: number;
  uTex: ReturnType<Regl['texture']>;
  uFitMode: number;
  uSourceAspect: number;
  uTargetAspect: number;
  uSourceRegion: [number, number, number, number];
  uMediaScale: number;
  uMediaOffset: [number, number];
  uGlobalMediaTransform: number;
  uFadeEdges: [number, number, number, number];
  uHeatmap: number;
  uResolution: [number, number];
  uUseMask: number;
  uMask: ReturnType<Regl['texture']>;
};

type DrawAttributes = {
  aPos: Buffer;
  aUV: Buffer;
};

type DrawProps = DrawUniforms & DrawAttributes & { elements: Elements };

type GeometryCache = {
  key: string;
  pos: Buffer;
  uv: Buffer;
  elements: Elements;
};

const ZERO2: [number, number] = [0, 0];

const EMPTY_FINE_UNIFORMS: FineWarpUniformsV2 = {
  uFineCount: 0,
  uFineDest0: ZERO2, uFineDest1: ZERO2, uFineDest2: ZERO2, uFineDest3: ZERO2,
  uFineDest4: ZERO2, uFineDest5: ZERO2, uFineDest6: ZERO2, uFineDest7: ZERO2,
  uFineDelta0: ZERO2, uFineDelta1: ZERO2, uFineDelta2: ZERO2, uFineDelta3: ZERO2,
  uFineDelta4: ZERO2, uFineDelta5: ZERO2, uFineDelta6: ZERO2, uFineDelta7: ZERO2,
  uFineRadius0: 0.001, uFineRadius1: 0.001, uFineRadius2: 0.001, uFineRadius3: 0.001,
  uFineRadius4: 0.001, uFineRadius5: 0.001, uFineRadius6: 0.001, uFineRadius7: 0.001,
};

export function createWarpV2Renderer(canvas: HTMLCanvasElement): WarpV2Renderer {
  const regl: Regl = createREGL({
    canvas,
    extensions: ['OES_standard_derivatives'],
    attributes: {
      antialias: true,
      preserveDrawingBuffer: false,
      premultipliedAlpha: false,
    },
  });

  // surface별 geometry를 보관한다. regl command 생성 시 고정 elements를 물리면
  // 최초 quad의 index count(6)가 캡처되어 이후 mesh tessellation 업로드가 실제
  // draw에 반영되지 않을 수 있으므로 attributes/elements 모두 draw prop으로 전달한다.
  const geometryCache = new Map<string, GeometryCache>();

  function geometryFor(surface: Surface, bypass: boolean): GeometryCache {
    const key = bypass
      ? 'bypass'
      : JSON.stringify(surface.mesh ?? surface.corners);
    const cached = geometryCache.get(surface.id);
    if (cached?.key === key) return cached;
    if (cached) {
      cached.pos.destroy();
      cached.uv.destroy();
      cached.elements.destroy();
    }
    const geo = bypass
      ? buildSurfaceGeometry(null)
      : surface.mesh
        ? buildSurfaceGeometry(surface.mesh)
        : buildSurfacePatchGeometry(surface.corners);
    const next: GeometryCache = {
      key,
      pos: regl.buffer(geo.positions),
      uv: regl.buffer(geo.uvs),
      elements: regl.elements(geo.indices),
    };
    geometryCache.set(surface.id, next);
    return next;
  }

  // -------- 텍스처 (샘플러 uniform은 항상 유효한 텍스처가 필요) --------
  const fallbackTex = regl.texture({ width: 1, height: 1, data: [0, 0, 0, 255] });
  const fallbackMask = regl.texture({
    width: 1,
    height: 1,
    data: [255, 255, 255, 255],
  });

  // 미디어 텍스처 — 엘리먼트 크기가 바뀌면 재생성, 아니면 subimage 갱신.
  let mediaTex: ReturnType<Regl['texture']> | null = null;
  let mediaTexSize = '';
  let mediaTexElement: MediaTextureSourceV2['element'] | null = null;

  function resetMediaTexture(): void {
    mediaTex?.destroy();
    mediaTex = null;
    mediaTexSize = '';
    mediaTexElement = null;
  }

  function mediaTexture(media: MediaTextureSourceV2): ReturnType<Regl['texture']> {
    const sizeKey = `${media.width}x${media.height}`;
    try {
      // 서로 다른 미디어가 같은 해상도인 경우에도 DOM source는 별개다.
      // 이전 video element에 묶인 texture를 재사용하면 일부 GPU/브라우저에서
      // subimage가 계속 실패하고 검은 fallback만 남으므로 반드시 재생성한다.
      if (!mediaTex || mediaTexSize !== sizeKey || mediaTexElement !== media.element) {
        resetMediaTexture();
        // video element에서 shape를 추론하게 두면 디코더가 프레임을 교체하는
        // 찰나 regl이 `invalid texture shape`를 낼 수 있다. 확정된 메타데이터
        // 크기로 먼저 할당한 뒤 현재 프레임을 업로드하면 전환 시에도 안정적이다.
        mediaTex = regl.texture({
          width: media.width,
          height: media.height,
          flipY: false,
        });
        mediaTex.subimage(media.element);
        mediaTexSize = sizeKey;
        mediaTexElement = media.element;
      } else {
        // 비디오 현재 프레임 갱신 (이미지에도 무해 — 동일 픽셀 재업로드).
        mediaTex.subimage(media.element);
      }
      return mediaTex;
    } catch {
      // 한 번의 프레임 업로드 실패가 영구적인 검은 화면으로 고착되지 않게
      // 캐시를 비운다. 다음 draw에서 현재 element로 texture를 새로 만든다.
      resetMediaTexture();
      return fallbackTex;
    }
  }

  // 마스크 텍스처 캐시 — surface.id별. rasterizeMask는 DOM canvas 2D 사용.
  const maskCanvas = document.createElement('canvas');
  const maskTextures = new Map<
    string,
    { key: string; tex: ReturnType<Regl['texture']> }
  >();

  function maskTexture(
    surface: Surface,
    quality: 'draft' | 'full',
  ): ReturnType<Regl['texture']> | null {
    const mask = surface.mask;
    if (!mask) return null;
    const baseW = Math.max(64, Math.min(MASK_MAX_WIDTH, canvas.width || 1024));
    const w = quality === 'draft' ? Math.max(64, Math.floor(baseW / 4)) : baseW;
    const h = Math.max(
      64,
      Math.round((w * (canvas.height || 576)) / Math.max(1, canvas.width || 1024)),
    );
    const key = `${JSON.stringify(mask)}|${w}x${h}`;
    const cached = maskTextures.get(surface.id);
    if (cached && cached.key === key) return cached.tex;

    // feather는 기준 출력 해상도(3840px) 기준 px — 래스터 캔버스 폭에 비례 축소.
    const feather = (mask.feather * w) / FEATHER_REF_WIDTH;
    rasterizeMask({ ...mask, feather }, maskCanvas, w, h);
    let tex: ReturnType<Regl['texture']>;
    if (cached) {
      cached.tex({ data: maskCanvas, flipY: false });
      tex = cached.tex;
    } else {
      tex = regl.texture({ data: maskCanvas, flipY: false });
    }
    maskTextures.set(surface.id, { key, tex });
    return tex;
  }

  const cmd = regl<DrawUniforms, DrawAttributes, DrawProps>({
    vert: WARP2_VERT,
    frag: WARP2_FRAG,
    attributes: {
      aPos: regl.prop<DrawProps, 'aPos'>('aPos'),
      aUV: regl.prop<DrawProps, 'aUV'>('aUV'),
    },
    elements: regl.prop<DrawProps, 'elements'>('elements'),
    uniforms: {
      uHinv: regl.prop<DrawProps, 'uHinv'>('uHinv'),
      uUseMesh: regl.prop<DrawProps, 'uUseMesh'>('uUseMesh'),
      uPattern: regl.prop<DrawProps, 'uPattern'>('uPattern'),
      uUseTexture: regl.prop<DrawProps, 'uUseTexture'>('uUseTexture'),
      uTex: regl.prop<DrawProps, 'uTex'>('uTex'),
      uFitMode: regl.prop<DrawProps, 'uFitMode'>('uFitMode'),
      uSourceAspect: regl.prop<DrawProps, 'uSourceAspect'>('uSourceAspect'),
      uTargetAspect: regl.prop<DrawProps, 'uTargetAspect'>('uTargetAspect'),
      uSourceRegion: regl.prop<DrawProps, 'uSourceRegion'>('uSourceRegion'),
      uMediaScale: regl.prop<DrawProps, 'uMediaScale'>('uMediaScale'),
      uMediaOffset: regl.prop<DrawProps, 'uMediaOffset'>('uMediaOffset'),
      uGlobalMediaTransform: regl.prop<DrawProps, 'uGlobalMediaTransform'>('uGlobalMediaTransform'),
      uFadeEdges: regl.prop<DrawProps, 'uFadeEdges'>('uFadeEdges'),
      uHeatmap: regl.prop<DrawProps, 'uHeatmap'>('uHeatmap'),
      uResolution: regl.prop<DrawProps, 'uResolution'>('uResolution'),
      uUseMask: regl.prop<DrawProps, 'uUseMask'>('uUseMask'),
      uMask: regl.prop<DrawProps, 'uMask'>('uMask'),
      uFineCount: regl.prop<DrawProps, 'uFineCount'>('uFineCount'),
      uFineDest0: regl.prop<DrawProps, 'uFineDest0'>('uFineDest0'), uFineDest1: regl.prop<DrawProps, 'uFineDest1'>('uFineDest1'),
      uFineDest2: regl.prop<DrawProps, 'uFineDest2'>('uFineDest2'), uFineDest3: regl.prop<DrawProps, 'uFineDest3'>('uFineDest3'),
      uFineDest4: regl.prop<DrawProps, 'uFineDest4'>('uFineDest4'), uFineDest5: regl.prop<DrawProps, 'uFineDest5'>('uFineDest5'),
      uFineDest6: regl.prop<DrawProps, 'uFineDest6'>('uFineDest6'), uFineDest7: regl.prop<DrawProps, 'uFineDest7'>('uFineDest7'),
      uFineDelta0: regl.prop<DrawProps, 'uFineDelta0'>('uFineDelta0'), uFineDelta1: regl.prop<DrawProps, 'uFineDelta1'>('uFineDelta1'),
      uFineDelta2: regl.prop<DrawProps, 'uFineDelta2'>('uFineDelta2'), uFineDelta3: regl.prop<DrawProps, 'uFineDelta3'>('uFineDelta3'),
      uFineDelta4: regl.prop<DrawProps, 'uFineDelta4'>('uFineDelta4'), uFineDelta5: regl.prop<DrawProps, 'uFineDelta5'>('uFineDelta5'),
      uFineDelta6: regl.prop<DrawProps, 'uFineDelta6'>('uFineDelta6'), uFineDelta7: regl.prop<DrawProps, 'uFineDelta7'>('uFineDelta7'),
      uFineRadius0: regl.prop<DrawProps, 'uFineRadius0'>('uFineRadius0'), uFineRadius1: regl.prop<DrawProps, 'uFineRadius1'>('uFineRadius1'),
      uFineRadius2: regl.prop<DrawProps, 'uFineRadius2'>('uFineRadius2'), uFineRadius3: regl.prop<DrawProps, 'uFineRadius3'>('uFineRadius3'),
      uFineRadius4: regl.prop<DrawProps, 'uFineRadius4'>('uFineRadius4'), uFineRadius5: regl.prop<DrawProps, 'uFineRadius5'>('uFineRadius5'),
      uFineRadius6: regl.prop<DrawProps, 'uFineRadius6'>('uFineRadius6'), uFineRadius7: regl.prop<DrawProps, 'uFineRadius7'>('uFineRadius7'),
    },
  });

  function draw(input: WarpV2DrawInput): void {
    resize();
    regl.poll();
    regl.clear({ color: [0, 0, 0, 0], depth: 1 });

    const tex = input.media ? mediaTexture(input.media) : fallbackTex;
    const targetAspect =
      canvas.height > 0 ? canvas.width / canvas.height : 16 / 9;
    const scheduleTransform = input.surfaces.find(
      (surface) => surface.content.type === 'schedule',
    )?.mediaTransform;

    for (const surface of input.surfaces) {
      let Hinv: number[];
      if (input.bypassWarp) {
        Hinv = IDENTITY_HINV;
      } else {
        try {
          const H = computeHomography(UNIT_SOURCE_QUAD, surface.corners);
          Hinv = toColumnMajor(invert3x3(H));
        } catch {
          continue; // degenerate quad — surface 스킵
        }
      }

      const geometry = geometryFor(surface, input.bypassWarp);
      const mask = input.bypassWarp ? null : maskTexture(surface, input.maskQuality);
      const fine =
        input.bypassWarp || surface.fineWarpPoints.length === 0
          ? EMPTY_FINE_UNIFORMS
          : fineWarpUniformsV2(Hinv, surface.fineWarpPoints);
      const region = surface.sourceRegion ?? { x: 0, y: 0, width: 1, height: 1 };
      const fadeEdges: [number, number, number, number] =
        surface.content.type === 'schedule'
          ? [
              region.x <= 1e-6 ? 1 : 0,
              region.y <= 1e-6 ? 1 : 0,
              region.x + region.width >= 1 - 1e-6 ? 1 : 0,
              region.y + region.height >= 1 - 1e-6 ? 1 : 0,
            ]
          : [1, 1, 1, 1];

      cmd({
        aPos: geometry.pos,
        aUV: geometry.uv,
        elements: geometry.elements,
        uHinv: Hinv,
        uUseMesh: input.bypassWarp ? 0 : 1,
        uPattern: PATTERN_ID_V2[input.pattern],
        uUseTexture: input.media ? 1 : 0,
        uTex: tex,
        uFitMode: input.media ? fitModeToUniform(input.media.fit) : 0,
        uSourceAspect: input.media
          ? surface.content.type === 'schedule'
            ? input.media.width / Math.max(1, input.media.height)
            : (input.media.width / Math.max(1, input.media.height)) *
              ((surface.sourceRegion?.width ?? 1) / (surface.sourceRegion?.height ?? 1))
          : 0,
        uTargetAspect: surface.content.type === 'schedule'
          ? targetAspect
          : sourceRegionTargetAspect(surface.sourceRegion, targetAspect),
        uSourceRegion: [
          surface.sourceRegion?.x ?? 0,
          surface.sourceRegion?.y ?? 0,
          surface.sourceRegion?.width ?? 1,
          surface.sourceRegion?.height ?? 1,
        ],
        uMediaScale: (surface.content.type === 'schedule'
          ? scheduleTransform
          : surface.mediaTransform)?.scale ?? 1,
        uMediaOffset: [
          (surface.content.type === 'schedule' ? scheduleTransform : surface.mediaTransform)?.offsetX ?? 0,
          (surface.content.type === 'schedule' ? scheduleTransform : surface.mediaTransform)?.offsetY ?? 0,
        ],
        uGlobalMediaTransform: surface.content.type === 'schedule' ? 1 : 0,
        uFadeEdges: fadeEdges,
        uHeatmap: input.heatmap ? 1 : 0,
        uResolution: [canvas.width, canvas.height],
        uUseMask: mask ? 1 : 0,
        uMask: mask ?? fallbackMask,
        ...fine,
      });
    }

    const activeIds = new Set(input.surfaces.map((surface) => surface.id));
    for (const [id, geometry] of geometryCache) {
      if (activeIds.has(id)) continue;
      geometry.pos.destroy();
      geometry.uv.destroy();
      geometry.elements.destroy();
      geometryCache.delete(id);
    }
  }

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
  }

  return {
    draw,
    resize,
    destroy: () => {
      resetMediaTexture();
      for (const geometry of geometryCache.values()) {
        geometry.pos.destroy();
        geometry.uv.destroy();
        geometry.elements.destroy();
      }
      geometryCache.clear();
      for (const cached of maskTextures.values()) cached.tex.destroy();
      maskTextures.clear();
      fallbackTex.destroy();
      fallbackMask.destroy();
      regl.destroy();
    },
  };
}
